"use client";

import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Fade,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UserInfo from "../../ClientDashboard/UserManagement/AddUser/UserInfo";
import { useNavigate } from "react-router-dom";
import config from "../../../config";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

const EditProfile = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);

  // pull the current user’s id from your config (supports either key)
  const profileUserId =
    config?.user?.client_user_id || config?.user?.user_id || "";

  // form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);

  // Original values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    profilePicture: null
  });

  // ui states
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const CLIENT_ID = config.clientId;

  const handleChange = (_e, newValue) => setValue(newValue);

  // prefill (edit only)
  useEffect(() => {
    if (!profileUserId) {
      setSnackbarMsg("Missing user id for profile.");
      setSnackbarOpen(true);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/client/user/${profileUserId}`, {
          headers: config.getHeaders()
        });
        const json = await res.json();
        if (res.ok && json?.data) {
          const u = json.data;
          const firstName_val = u.first_name || "";
          const lastName_val = u.last_name || "";
          const email_val = u.email_id || "";
          const phone_val = u.phone_number || "";
          const password_val = "";

          let picture_val = null;
          if (u.profile_picture_url) {
            const pictureUrl = u.profile_picture_url.startsWith('/')
              ? `${config.API_BASE_URL}${u.profile_picture_url}`
              : u.profile_picture_url;
            picture_val = pictureUrl;
          }

          // Set form values
          setFirstName(firstName_val);
          setLastName(lastName_val);
          setEmail(email_val);
          setPhone(phone_val);
          setPassword(password_val);
          setProfilePicture(picture_val);

          // Store original values for cancel functionality
          setOriginalValues({
            firstName: firstName_val,
            lastName: lastName_val,
            email: email_val,
            phone: phone_val,
            password: password_val,
            profilePicture: picture_val
          });
        } else {
          setSnackbarMsg(json?.message || "Failed to load profile details");
          setSnackbarOpen(true);
        }
      } catch (e) {
        setSnackbarMsg("Error fetching profile details");
        setSnackbarOpen(true);
      }
    })();
  }, [profileUserId]);

  const handleCancel = () => {
    // Reset all form fields to original values
    setFirstName(originalValues.firstName);
    setLastName(originalValues.lastName);
    setEmail(originalValues.email);
    setPhone(originalValues.phone);
    setPassword(originalValues.password);
    setProfilePicture(originalValues.profilePicture);
  };

  const handleSave = async () => {
    // validations handled by UserInfo and isFormValid state
    if (!isFormValid) {
      setSnackbarMsg("Please fix validation errors before saving.");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("client_id", CLIENT_ID);
    formData.append("client_user_id", profileUserId);
    formData.append("first_name", firstName.trim());
    formData.append("last_name", lastName.trim());
    formData.append("email_id", email.trim());
    formData.append("phone_number", phone.trim() || "");
    if (password) formData.append("password", password);

    // only append a new file; strings are existing URLs
    if (profilePicture && typeof profilePicture !== "string") {
      formData.append("profile_picture", profilePicture);
    }

    try {
      setLoading(true);
      const headers = config.getHeaders();
      delete headers["Content-Type"]; // Allow browser to set boundary for FormData

      const res = await fetch(`${config.API_BASE_URL}/client/user/update`, {
        method: "PATCH",
        headers: headers,
        body: formData,
      });
      const json = await res.json();

      if (!res.ok || json?.success === false) {
        const errMsg = json?.message || "Failed to update profile";
        const details = json?.error?.details;
        setSnackbarMsg(details ? `${errMsg}: ${details}` : errMsg);
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMsg(json?.message || "Profile updated successfully.");
      setSnackbarOpen(true);

      // Dispatch event with new profile picture URL so avatar updates immediately
      window.dispatchEvent(
        new CustomEvent("profile:updated", {
          detail: {
            first_name: firstName.trim(),
            user_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            profile_picture_url: json?.data?.profile_picture_url,
          },
        })
      );

      // Navigate back after giving avatar time to refresh
      setTimeout(() => navigate(-2), 1500);
    } catch (e) {
      setSnackbarMsg(e?.message || "Network error updating profile");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const tabLabels = ["Profile Info"];

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "25px" }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
        >
          Edit Profile
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Box>

      <Tabs value={value} onChange={handleChange}>
        {tabLabels.map((label, index) => (
          <Tab key={index} label={label} />
        ))}
      </Tabs>

      <CustomTabPanel value={value} index={0}>
        <UserInfo
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          password={password}
          setPassword={setPassword}
          profilePicture={profilePicture}
          setProfilePicture={setProfilePicture}
          isEdit={true}
          onValidationChange={setIsFormValid}
        />
      </CustomTabPanel>

      <Stack direction="row" alignItems="center" justifyContent="center" gap={2} mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !isFormValid}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </Stack>

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        autoHideDuration={3000}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Card>
  );
};

export default EditProfile;
